const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// The backend wraps responses in a data wrapper
interface DataWrapper<T> {
  data: T;
}

export interface PaginatedDataWrapper<T> {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  data: T;
}

export interface User {
  id: string;
  email: string;
  name?: string;
  created_at: string;
}

export interface LoginResponse {
  user: User;
  token: string;
}

export interface Question {
  id: string;
  title: string;
  description?: string;
  is_public: boolean;
  created_by: string;
  created_at: string;
  dependent_variables?: Variable[];
  independent_variables?: Variable[];
}

export interface Variable {
  id: string;
  name: string;
  description?: string;
  type: 'numeric' | 'categorical' | 'boolean';
  dependent: boolean;
  created_by: string;
  created_at: string;
}

export interface DataPointValue {
  value: number | string | boolean;
  description?: string;
}

export interface DataPoint {
  id: string;
  created_by: string;
  created_at: string;
  values: Record<string, DataPointValue>;
}

export interface CorrelationResult {
  method: string;
  effect_size: number;
  p_value: number;
  sample_size: number;
  error?: string;
}

export interface Dataset {
  variables: Record<string, Variable>;
  datapoints: DataPoint[];
  correlations?: Record<string, Record<string, CorrelationResult>>;
}

class APIClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private async handleError(response: Response, fallbackMessage: string): Promise<never> {
    let serverMessage: string | null = null;
    
    try {
      const errorData = await response.json();
      serverMessage = errorData.error || errorData.message || null;
    } catch (parseError) {
      // If we can't parse the response, just use the fallback
    }

    if (serverMessage) {
      throw new Error(`${fallbackMessage}: ${serverMessage}`);
    } else {
      throw new Error(fallbackMessage);
    }
  }

  // Authentication
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/v1/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Login failed');
    }

    const wrapper: DataWrapper<LoginResponse> = await response.json();
    return wrapper.data;
  }

  async createUser(email: string, password: string, name?: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/v1/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Failed to create user');
    }

    const wrapper: DataWrapper<User> = await response.json();
    return wrapper.data;
  }

  // Questions
  async listQuestions(): Promise<Question[]> {
    const response = await fetch(`${API_BASE_URL}/v1/questions`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch questions');
    const wrapper: DataWrapper<Question[]> = await response.json();
    return wrapper.data;
  }

  async getQuestion(id: string): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  async createQuestion(data: {
    title: string;
    description?: string;
    is_public: boolean;
  }): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  async updateQuestion(id: string, data: {
    title?: string;
    description?: string;
    is_public?: boolean;
  }): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to update question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  async deleteQuestion(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete question');
  }

  // Variables
  async listVariables(questionId: string): Promise<Variable[]> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/variables`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch variables');
    const wrapper: DataWrapper<Variable[]> = await response.json();
    return wrapper.data;
  }

  async createVariable(questionId: string, data: {
    name: string;
    description?: string;
    type: 'numeric' | 'categorical' | 'boolean';
    dependent: boolean;
  }): Promise<Variable> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/variables`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create variable');
    const wrapper: DataWrapper<Variable> = await response.json();
    return wrapper.data;
  }

  async deleteVariable(variableId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/variables/${variableId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete variable');
  }

  // Dataset (datapoints, variables, correlations)
  async listDataset(
    questionId: string,
    page?: number,
    perPage?: number
  ): Promise<PaginatedDataWrapper<Dataset>> {
    const params = new URLSearchParams();
    if (page !== undefined) {
      params.set('page', String(page));
    }
    if (perPage !== undefined) {
      params.set('per_page', String(perPage));
    }
    const query = params.toString();
    const url = query.length > 0
      ? `${API_BASE_URL}/v1/questions/${questionId}/datapoints?${query}`
      : `${API_BASE_URL}/v1/questions/${questionId}/datapoints`;

    const response = await fetch(url, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch dataset');
    const wrapper: PaginatedDataWrapper<Dataset> = await response.json();
    return wrapper;
  }

  async createDataPoint(
    questionId: string,
    values: Record<string, DataPointValue>,
    recordedAt: string
  ): Promise<DataPoint> {
    const body = {
      values,
      recorded_at: recordedAt,
    };

    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/datapoints`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create data point');
    const wrapper: DataWrapper<DataPoint> = await response.json();
    return wrapper.data;
  }

  async updateDataPoint(
    datapointId: string,
    data: {
      values?: Record<string, DataPointValue>;
      recorded_at?: string;
    }
  ): Promise<DataPoint> {
    const response = await fetch(`${API_BASE_URL}/v1/datapoints/${datapointId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to update data point');
    const wrapper: DataWrapper<DataPoint> = await response.json();
    return wrapper.data;
  }

  async deleteDataPoint(datapointId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/datapoints/${datapointId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete data point');
  }

}

export const api = new APIClient();
